// Use optimized image path instead of large image
// import trainingImage9Large from '../../assets/images/photos/Training/image9-large.jpeg';
import ImageText from '../image-text';
import './home.scss';
import './section-connector.scss';
import GlobalStats from '../global-stats/GlobalStats';
import ContactUs from '../contact/ContactUs';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Home = () => {
  // Create a ref for the contact section
  const contactSectionRef = useRef(null);
  const location = useLocation(); 
  
  // Add a special effect for page refresh
  useEffect(() => {
    // Handle initial page load/refresh
    // The performance navigation type 1 indicates a page refresh
    if (performance?.navigation?.type === 1 && location.pathname === '/') {
      console.log('🔄 HOME: Page refresh detected, ensuring scroll to top');
      window.scrollTo(0, 0);
      
      // Schedule multiple scroll attempts to ensure we stay at top
      const scrollAttempts = [50, 100, 250, 500];
      scrollAttempts.forEach(delay => {
        setTimeout(() => window.scrollTo(0, 0), delay);
      });
    }
  }, []);

  // Handle scrolling to contact section when component mounts or URL changes
  useEffect(() => {
    // Check if this navigation came from a logo click
    const locationState = window.history.state?.usr || {};
    const isLogoNavigation = locationState?.fromLogoClick === true;
    
    // Logo click always takes precedence - scroll to top and exit
    if (isLogoNavigation) {
      console.log('🏠 HOME: Logo click navigation detected, forcing scroll to top');
      window.scrollTo({top: 0, behavior: 'instant'});
      
      // Schedule multiple scroll attempts to ensure we stay at the top
      setTimeout(() => window.scrollTo({top: 0, behavior: 'instant'}), 100);
      setTimeout(() => window.scrollTo({top: 0, behavior: 'instant'}), 300);
      return;
    }
    
    // Check if this is an explicit navigation to the homepage with no hash
    // Including initial page load/refresh with no parameters
    const isExplicitHomeNavigation = location.pathname === '/' && 
                                   (!location.hash || location.hash === '') && 
                                   !location.search;
                                   
    // If explicit navigation to homepage root, scroll to top and exit early
    if (isExplicitHomeNavigation) {
      console.log('🏠 HOME: Explicit navigation to homepage root or refresh, scrolling to top');
      window.scrollTo(0, 0);
      
      // Also schedule a follow-up scroll to ensure we stay at top
      setTimeout(() => window.scrollTo(0, 0), 50);
      return;
    }// For all other cases, we'll handle hash/query params
    const hash = location.hash;
    const hasContactHash = hash.includes('contact');
    
    // We already have locationState from above, so just check additional conditions
    const shouldSkipContactScroll = locationState?.skipContactScroll === true || 
                                   locationState?.fromLogoClick === true;
    
    if (shouldSkipContactScroll) {
      console.log('🏠 HOME: Skipping contact scroll due to navigation state flags');
      return;
    }
      // Check for skipContactScroll flag which would be set by logo click
    const shouldSkipScrollToContact = locationState?.skipContactScroll === true;
    
    // Backward compatibility - check for scrollToContact parameter in URL
    const searchParams = new URLSearchParams(location.search);
    const shouldScrollToContact = searchParams.get('scrollToContact') === 'true';
    
    // If should scroll to contact and not explicitly skipped, update URL to use hash instead
    if (shouldScrollToContact && !hasContactHash && !shouldSkipScrollToContact && window.history) {
      // Replace the query param with a hash but preserve navigation state
      window.history.replaceState(
        window.history.state?.usr || {}, 
        document.title, 
        '/#contact'
      );
      // Let the ScrollToTop component handle the actual scrolling
      return;
    }
    
    // If coming from direct contact hash link, the ScrollToTop component
    // will already handle scrolling to the element - we don't need to do anything
    
    // For any other navigation to home without params, ensure we're at the top
    if (!hasContactHash && !shouldScrollToContact) {
      window.scrollTo(0, 0);
    }
    
    // Clean up URL params after handling them to prevent persistence
    if (shouldScrollToContact && window.history && window.history.replaceState) {
      window.history.replaceState({}, document.title, '/#contact');
    }
  }, [location]);
  return (
    <>    <Helmet>
      <title>Technical Training & Consulting in GenAI, Cloud & Software | Watchelm</title>
      <meta name="description" content="Watchelm delivers tailored technical training and consulting in Generative AI, cloud, software development and architecture. We help engineering and data teams in enterprises adopt new technologies with confidence through instructor-led courses and customised programs." />
      <link rel="canonical" href="https://www.watchelm.com/"></link>
         {/* Organization Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Watchelm Consulting Ltd",
          "url": "https://www.watchelm.com",
          "logo": "https://www.watchelm.com/assets/images/watchelmlogo-with-strap.svg",
          "sameAs": [
            "https://www.linkedin.com/company/watchelm"
          ]
        })}
      </script>
      
      {/* WebSite Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "url": "https://www.watchelm.com",
          "name": "Watchelm"
        })}
      </script>
    </Helmet>
    
    <div className="flow-page">      {/* First Feature section */}
      <div className="section-container">
        <ImageText
          title="Solving Problems Through Technology Training, Consultancy, and EdTech."
          subtitle=""
          content="<p>Our expert-led training programs are designed to give your team the edge in today's competitive landscape. With a focus on practical, hands-on learning, we ensure your staff gain applicable skills they can implement immediately.</p><ul><li>Customized curriculum to meet your specific needs</li><li>Expert instructors with real-world experience</li><li>Flexible delivery options - onsite, virtual, or hybrid</li></ul>"
          imageSrc="/assets/images/optimized/Training/image9"
          imageAlt="AWS DevOps Training"
          buttonText="Learn More"
          buttonLink="/training"
          buttonText2="Get in Touch Today"
          buttonLink2="/contact"
          imageOnRight={false}
          gradientTitle="green"
          className="training-section"
          subjectType="training"
        />{/* Zigzag connector to the right */}
        <div className="section-connector zigzag-right">
          {/* Arrow head removed */}
        </div>
      </div>      {/* Second Training section */}
      <div className="section-container">
        <ImageText 
          title="We Excel Together through Leading Tech Training"
          subtitle="Generative AI - Data Engineering - Cloud Computing - Software Development - Cybersecurity - Altova"
          content="<p>Our training programs are designed to empower your team with the skills they need to excel in today's fast-paced tech landscape. We focus on practical, hands-on learning that can be applied immediately to real-world challenges.</p><p>Whether you're looking to upskill your workforce or implement new technologies, our expert instructors are here to guide you every step of the way.</p>"
          useHeaderBackground={true}
          imageAlt="Technical Training"
          buttonText="Explore our Courses"
          buttonLink="/training"
          buttonText2="Get in Touch Today"
          buttonLink2="/contact"
          imageOnRight={true}
          backgroundColor="#f8f9fa"
          gradientTitle="red-to-green"
          className="training-section"
          backgroundClassName='header-style-background-training'
          subjectType="training"
        />
        {/* Zigzag connector to the left */}
        <div className="section-connector zigzag-left">
          {/* Arrow head removed */}
        </div>
      </div>

      {/* Third Feature section */}
      <div className="section-container">
        <ImageText 
          title="WE Solve Together Through Expert Technical Consultancy"
          subtitle="Embrace agility and drive innovation"
          content="<p>Our consulting services help you navigate complex technical challenges with confidence. Whether you're planning a cloud migration, implementing DevOps practices, or exploring AI solutions, our experts provide the guidance you need.</p><p>We collaborate closely with your team to understand your unique challenges and develop tailored strategies that deliver measurable results.</p><p>Our experienced coaches guide you through the implementation of frameworks like Scrum, Kanban, or hybrid approaches tailored to your specific needs and organizational culture.</p>"
          imageAlt="WatchElm Consulting"
          buttonText="Learn More"
          buttonLink="/consulting"
          buttonLink2="/contact"
          buttonText2="Get in Touch Today"
          imageOnRight={false}
          backgroundColor="white"
          gradientTitle="red-to-green"
          className="agile-section"
          useHeaderBackground={true}
          backgroundClassName='header-style-background-consulting'
          subjectType="consultancy"
        />
          {/* Final connecting line with marker at bottom */}
        <div className="section-connector bottom-marker">
          {/* Using the SVG-based styling - no inner elements needed */}
        </div></div>
         {/* Fourth and final section */}      <div className="section-container">
        <ImageText 
          title="We EDUCATE Together Through Tailored EdTech Solutions"
          subtitle="Smart Platforms - Scalable Tools - Digital Learning - Tech Enabled - User Centric - Innovative"
          content="<p>Our EdTech solutions are designed to empower learners and educators alike. We focus on creating user-centric
          platforms that facilitate engaging and effective learning experiences.</p>"
          useHeaderBackground={true}
          imageAlt="Technical Training"
          buttonText="Learn More"
          buttonLink="/edtech"
          buttonText2="Get in Touch Today"
          buttonLink2="/contact"
          imageOnRight={true}
          backgroundColor="#f8f9fa"
          gradientTitle="red-to-green"
          className="training-section"
          backgroundClassName='header-style-background-edtech'
          subjectType="edtech"
        />{/* Zigzag connector to the left */}
        <div className="section-connector zigzag-left">
          {/* Arrow head removed */}
        </div>      </div>      <GlobalStats />      
        
        <div id="contact" className="section-container contact-section" ref={contactSectionRef}>
        
          <div id="contact-form-wrapper">
            <div className="contact-wrapper">
              <div className="left-panel">
                <div className="text-container">
                  <div className="text-item">Rooted in Success.</div>
                  <div className="text-item">Rooted in Success.</div>
                  <div className="text-item focused">Rooted in Success.</div>
                  <div className="text-item">Rooted in Success.</div>
                  <div className="text-item">Rooted in Success.</div>
                </div>
            </div>
            <div className="right-panel">
              <ContactUs />
            </div>
          </div>
        </div>
      </div>
      
    </div>
    </>
  );
}

export default Home;
